import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { 
  MessageSquare, Plus, Search, Clock, Mail, Phone, 
  Trash2, Edit, User
} from 'lucide-react';

const API = "/api";

const CATEGORIES = [
  { value: 'all', label: 'All Posts', color: 'bg-gray-500' },
  { value: 'general', label: 'General', color: 'bg-blue-500' },
  { value: 'for-sale', label: 'For Sale', color: 'bg-green-500' },
  { value: 'wanted', label: 'Wanted', color: 'bg-yellow-500' },
  { value: 'events', label: 'Events', color: 'bg-purple-500' },
  { value: 'musicians', label: 'Musicians', color: 'bg-pink-500' },
  { value: 'services', label: 'Services', color: 'bg-orange-500' },
  { value: 'housing', label: 'Housing', color: 'bg-cyan-500' },
];

const CommunityBoard = () => {
  const { user, token } = useAuth();
  const [posts, setPosts] = useState([]);
  const [myPosts, setMyPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [showMyPosts, setShowMyPosts] = useState(false);
  
  const [newPost, setNewPost] = useState({
    title: '',
    content: '',
    category: 'general',
    image_url: '',
    contact_email: '',
    contact_phone: ''
  });

  useEffect(() => {
    fetchPosts();
    if (token) {
      fetchMyPosts();
    }
  }, [selectedCategory, token]);

  const fetchPosts = async () => {
    setLoading(true);
    try {
      const params = selectedCategory !== 'all' ? `?category=${selectedCategory}` : '';
      const response = await axios.get(`${API}/community/posts${params}`);
      setPosts(response.data);
    } catch (error) {
      console.error('Failed to fetch posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMyPosts = async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${API}/community/my-posts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMyPosts(response.data);
    } catch (error) {
      console.error('Failed to fetch my posts:', error);
    }
  };

  const handleCreatePost = async (e) => {
    e.preventDefault();
    if (!token) {
      toast.error('Please sign in to create a post');
      return;
    }
    
    try {
      await axios.post(`${API}/community/posts`, newPost, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Post created! It will be visible for 30 days.');
      setDialogOpen(false);
      setNewPost({
        title: '',
        content: '',
        category: 'general',
        image_url: '',
        contact_email: user?.email || '',
        contact_phone: ''
      });
      fetchPosts();
      fetchMyPosts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create post');
    }
  };

  const handleUpdatePost = async (e) => {
    e.preventDefault();
    if (!editingPost) return;
    
    try {
      await axios.put(`${API}/community/posts/${editingPost.id}`, newPost, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Post updated!');
      setDialogOpen(false);
      setEditingPost(null);
      setNewPost({
        title: '',
        content: '',
        category: 'general',
        image_url: '',
        contact_email: user?.email || '',
        contact_phone: ''
      });
      fetchPosts();
      fetchMyPosts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update post');
    }
  };

  const handleDeletePost = async (postId) => {
    if (!confirm('Are you sure you want to delete this post?')) return;
    
    try {
      await axios.delete(`${API}/community/posts/${postId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Post deleted');
      fetchPosts();
      fetchMyPosts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete post');
    }
  };

  const openEditDialog = (post) => {
    setEditingPost(post);
    setNewPost({
      title: post.title,
      content: post.content,
      category: post.category,
      image_url: post.image_url || '',
      contact_email: post.contact_email || '',
      contact_phone: post.contact_phone || ''
    });
    setDialogOpen(true);
  };

  const openCreateDialog = () => {
    setEditingPost(null);
    setNewPost({
      title: '',
      content: '',
      category: 'general',
      image_url: '',
      contact_email: user?.email || '',
      contact_phone: ''
    });
    setDialogOpen(true);
  };

  const getCategoryColor = (category) => {
    return CATEGORIES.find(c => c.value === category)?.color || 'bg-gray-500';
  };

  const getDaysRemaining = (expiresAt) => {
    const now = new Date();
    const expires = new Date(expiresAt);
    const diff = Math.ceil((expires - now) / (1000 * 60 * 60 * 24));
    return diff;
  };

  const filteredPosts = (showMyPosts ? myPosts : posts).filter(post =>
    post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    post.content.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-background p-4 md:p-6 pb-32">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6"
        >
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold flex items-center gap-3">
                <MessageSquare className="text-primary" />
                Community Board
              </h1>
              <p className="text-muted-foreground mt-1">
                Connect with the Twin Cities community. Posts expire after 30 days.
              </p>
            </div>
            
            {user ? (
              <Button onClick={openCreateDialog} className="gap-2">
                <Plus size={18} /> Create Post
              </Button>
            ) : (
              <p className="text-sm text-muted-foreground">
                Sign in to create a post
              </p>
            )}
          </div>
        </motion.div>

        {/* Filters */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.1 }}
          className="mb-6 space-y-4"
        >
          {/* Search and My Posts Toggle */}
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
              <Input
                placeholder="Search posts..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            {user && (
              <Button
                variant={showMyPosts ? "default" : "outline"}
                onClick={() => setShowMyPosts(!showMyPosts)}
                className="gap-2"
              >
                <User size={16} />
                {showMyPosts ? 'All Posts' : 'My Posts'}
              </Button>
            )}
          </div>

          {/* Category Filters */}
          <div className="flex flex-wrap gap-2">
            {CATEGORIES.map(cat => (
              <Button
                key={cat.value}
                variant={selectedCategory === cat.value ? "default" : "outline"}
                size="sm"
                onClick={() => setSelectedCategory(cat.value)}
                className="gap-1"
              >
                <span className={`w-2 h-2 rounded-full ${cat.color}`}></span>
                {cat.label}
              </Button>
            ))}
          </div>
        </motion.div>

        {/* Posts Grid */}
        {loading ? (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {[1, 2, 3, 4, 5, 6].map(i => (
              <Card key={i} className="animate-pulse bg-card">
                <CardContent className="p-4">
                  <div className="h-4 bg-muted rounded w-3/4 mb-2"></div>
                  <div className="h-3 bg-muted rounded w-full mb-1"></div>
                  <div className="h-3 bg-muted rounded w-2/3"></div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : filteredPosts.length === 0 ? (
          <Card className="bg-card border-border">
            <CardContent className="py-12 text-center">
              <MessageSquare size={48} className="mx-auto mb-4 text-muted-foreground" />
              <h3 className="text-lg font-semibold mb-2">No posts yet</h3>
              <p className="text-muted-foreground mb-4">
                {showMyPosts 
                  ? "You haven't created any posts yet." 
                  : "Be the first to post in the community!"}
              </p>
              {user && (
                <Button onClick={openCreateDialog}>
                  <Plus size={16} className="mr-2" /> Create Post
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            <AnimatePresence>
              {filteredPosts.map((post, index) => (
                <motion.div
                  key={post.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <Card className="bg-card border-border h-full flex flex-col hover:border-primary/50 transition-colors">
                    {post.image_url && (
                      <div className="relative h-40 overflow-hidden rounded-t-lg">
                        <img
                          src={post.image_url}
                          alt={post.title}
                          className="w-full h-full object-cover"
                          onError={(e) => e.target.style.display = 'none'}
                        />
                      </div>
                    )}
                    <CardHeader className="pb-2">
                      <div className="flex items-start justify-between gap-2">
                        <CardTitle className="text-lg line-clamp-2">{post.title}</CardTitle>
                        <Badge className={`${getCategoryColor(post.category)} text-white shrink-0`}>
                          {post.category}
                        </Badge>
                      </div>
                      <CardDescription className="flex items-center gap-2 text-xs">
                        <User size={12} />
                        {post.user_name}
                        <span className="text-muted-foreground">•</span>
                        <Clock size={12} />
                        {getDaysRemaining(post.expires_at)} days left
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="flex-1 flex flex-col">
                      <p className="text-sm text-muted-foreground line-clamp-3 mb-4 flex-1">
                        {post.content}
                      </p>
                      
                      {/* Contact Info */}
                      <div className="space-y-1 text-sm border-t border-border pt-3">
                        {post.contact_email && (
                          <a 
                            href={`mailto:${post.contact_email}`}
                            className="flex items-center gap-2 text-primary hover:underline"
                          >
                            <Mail size={14} />
                            {post.contact_email}
                          </a>
                        )}
                        {post.contact_phone && (
                          <a 
                            href={`tel:${post.contact_phone}`}
                            className="flex items-center gap-2 text-primary hover:underline"
                          >
                            <Phone size={14} />
                            {post.contact_phone}
                          </a>
                        )}
                      </div>

                      {/* Edit/Delete for own posts */}
                      {user && post.user_id === user.id && (
                        <div className="flex gap-2 mt-3 pt-3 border-t border-border">
                          <Button 
                            size="sm" 
                            variant="outline" 
                            className="flex-1"
                            onClick={() => openEditDialog(post)}
                          >
                            <Edit size={14} className="mr-1" /> Edit
                          </Button>
                          <Button 
                            size="sm" 
                            variant="destructive" 
                            className="flex-1"
                            onClick={() => handleDeletePost(post.id)}
                          >
                            <Trash2 size={14} className="mr-1" /> Delete
                          </Button>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}

        {/* Create/Edit Post Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingPost ? 'Edit Post' : 'Create New Post'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={editingPost ? handleUpdatePost : handleCreatePost} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="title">Title *</Label>
                <Input
                  id="title"
                  value={newPost.title}
                  onChange={(e) => setNewPost({...newPost, title: e.target.value})}
                  placeholder="What's this about?"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="category">Category</Label>
                <select
                  id="category"
                  className="w-full p-2 rounded border bg-background"
                  value={newPost.category}
                  onChange={(e) => setNewPost({...newPost, category: e.target.value})}
                >
                  {CATEGORIES.filter(c => c.value !== 'all').map(cat => (
                    <option key={cat.value} value={cat.value}>{cat.label}</option>
                  ))}
                </select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="content">Content *</Label>
                <textarea
                  id="content"
                  className="w-full p-2 rounded border bg-background min-h-[120px]"
                  value={newPost.content}
                  onChange={(e) => setNewPost({...newPost, content: e.target.value})}
                  placeholder="Describe what you're posting about..."
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="image_url">Image URL (optional)</Label>
                <Input
                  id="image_url"
                  value={newPost.image_url}
                  onChange={(e) => setNewPost({...newPost, image_url: e.target.value})}
                  placeholder="https://..."
                />
                {newPost.image_url && (
                  <img 
                    src={newPost.image_url} 
                    alt="Preview" 
                    className="w-full h-32 object-cover rounded mt-2"
                    onError={(e) => e.target.style.display = 'none'}
                  />
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="contact_email">Contact Email</Label>
                  <Input
                    id="contact_email"
                    type="email"
                    value={newPost.contact_email}
                    onChange={(e) => setNewPost({...newPost, contact_email: e.target.value})}
                    placeholder="your@email.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="contact_phone">Phone (optional)</Label>
                  <Input
                    id="contact_phone"
                    value={newPost.contact_phone}
                    onChange={(e) => setNewPost({...newPost, contact_phone: e.target.value})}
                    placeholder="612-555-1234"
                  />
                </div>
              </div>

              <div className="bg-muted/50 p-3 rounded text-sm text-muted-foreground">
                <Clock size={14} className="inline mr-1" />
                Posts are automatically removed after 30 days.
              </div>

              <Button type="submit" className="w-full">
                {editingPost ? 'Update Post' : 'Create Post'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default CommunityBoard;
